import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';

type Row = { countryCode: string; currency: string };

const ALL = '__ALL__';

export type TableFiltersProps<T extends Row> = {
  rows: readonly T[];
  countryParam: string;
  currencyParam: string;
};

/**
 * Filtros país/moeda persistidos em URL — compartilhados entre as tabelas
 * /sales. URL é fonte da verdade.
 */
export function TableFilters<T extends Row>({
  rows,
  countryParam,
  currencyParam,
}: TableFiltersProps<T>): React.JSX.Element {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const country = params.get(countryParam) ?? '';
  const currency = params.get(currencyParam) ?? '';

  const countryOptions = useMemo(
    () => [
      { value: ALL, label: t('sales.tables.filters.allCountries') },
      ...Array.from(new Set(rows.map((r) => r.countryCode).filter(Boolean)))
        .sort()
        .map((c) => ({ value: c, label: c })),
    ],
    [rows, t],
  );
  const currencyOptions = useMemo(
    () => [
      { value: ALL, label: t('sales.tables.filters.allCurrencies') },
      ...Array.from(new Set(rows.map((r) => r.currency).filter(Boolean)))
        .sort()
        .map((c) => ({ value: c, label: c })),
    ],
    [rows, t],
  );

  function updateParam(key: string, value: string): void {
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        if (value === '') merged.delete(key);
        else merged.set(key, value);
        return merged;
      },
      { replace: false },
    );
  }

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Select
        ariaLabel={t('sales.tables.filters.country')}
        selectedOption={countryOptions.find((o) => o.value === (country || ALL)) ?? null}
        options={countryOptions}
        onChange={({ detail }): void => {
          const v = detail.selectedOption.value;
          updateParam(countryParam, v === ALL ? '' : (v ?? ''));
        }}
      />
      <Select
        ariaLabel={t('sales.tables.filters.currency')}
        selectedOption={currencyOptions.find((o) => o.value === (currency || ALL)) ?? null}
        options={currencyOptions}
        onChange={({ detail }): void => {
          const v = detail.selectedOption.value;
          updateParam(currencyParam, v === ALL ? '' : (v ?? ''));
        }}
      />
    </SpaceBetween>
  );
}
